import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';
import { Check, Copy, HandCoins, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import type { ClientWithdrawal, ClientWithdrawalFilter, ClientWithdrawalsPage } from '@/api/types';
import { BottomSheet } from '@/components/ui/dialog';
import { SegmentControl } from '@/components/ui/segment-control';
import { Skeleton, SkeletonCard, SkeletonLine } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import {
  REJECT_REASON_MAX,
  WITHDRAW_STATUSES,
  formatDisplayMinor,
  formatUsdt,
  isMaskedDestination,
  withdrawMethodShort,
  withdrawTarget,
} from '@/lib/client-withdraw';
import { getWebApp, haptic } from '@/lib/telegram';
import { MoneySwitch } from '@/components/SectionSwitch';

/**
 * Заявки клиентов на вывод реферального заработка (идея #162).
 *
 * Деньги переводит сам партнёр по реквизитам из заявки, здесь он только отмечает
 * итог: «Выплачено» переносит сумму из холда клиента ему в доход, «Отклонить» с
 * причиной возвращает её клиенту на баланс. Те же кнопки приходят в личку франш-бота.
 * Делегатам действия доступны: деньги при «Выплачено» идут к партнёру, а не от него.
 *
 * Перед переводом партнёр жмёт «Перевожу»: заявка уходит в работу, и клиент её больше
 * не отменит. Без этого шага клиент мог отменить заявку, пока деньги уже в пути, и
 * получить и перевод, и сумму обратно на баланс.
 *
 * GRAM и чек CryptoBot (заявки #183, #186) можно отдать платформе: она переводит сама,
 * комиссия из суммы клиента, баланс партнёра не меняется. У такой заявки кнопок нет,
 * только её статус.
 */
export const Route = createFileRoute('/client-withdrawals')({ component: ClientWithdrawals });

const CLOSED_CODE = 'app.referral_withdraw.closed';
const CLOSED_TEXT = 'По этой заявке уже есть решение';

function errorCode(e: unknown): string | undefined {
  return (e as { body?: { error?: { code?: string } } } | null)?.body?.error?.code;
}

function errorReason(e: unknown): string | undefined {
  const reason = (e as { body?: { error?: { context?: { reason?: unknown } } } } | null)?.body?.error?.context
    ?.reason;
  return typeof reason === 'string' ? reason : undefined;
}

function fmtDateTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* ниже запасной путь */
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

function ClientWithdrawals() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [status, setStatus] = useState<ClientWithdrawalFilter>('pending');
  const [paying, setPaying] = useState<ClientWithdrawal | null>(null);
  const [rejecting, setRejecting] = useState<ClientWithdrawal | null>(null);
  const [platforming, setPlatforming] = useState<ClientWithdrawal | null>(null);

  const list = useInfiniteQuery({
    queryKey: ['client-withdrawals', status],
    queryFn: ({ pageParam }) => api.clientWithdrawals({ status, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const { data: counts } = useQuery({
    queryKey: ['client-withdrawal-counts'],
    queryFn: () => api.clientWithdrawalCounts(),
    staleTime: 30_000,
  });
  // Нужны для пустого экрана: «вывод выключен» и «новых заявок нет» это разные советы.
  const { data: stats } = useQuery({ queryKey: ['referral-stats'], queryFn: () => api.referralStats() });
  const { data: partner } = useQuery({ queryKey: ['partner'], queryFn: () => api.me() });

  useEffect(() => {
    const tg = getWebApp();
    if (!tg?.BackButton) return;
    tg.BackButton.show();
    tg.MainButton.hide();
    // Сюда приходят и из профиля, и с баннера на странице рефералов: возвращаем туда,
    // откуда пришли. Без истории (открыли сразу сюда) ведём в «Деньги».
    const onBack = () => {
      if (window.history.length > 1) window.history.back();
      else void navigate({ to: '/finance' });
    };
    tg.BackButton.onClick(onBack);
    return () => tg.BackButton.offClick(onBack);
  }, [navigate]);

  const refreshAll = () => {
    void qc.invalidateQueries({ queryKey: ['client-withdrawals'] });
    void qc.invalidateQueries({ queryKey: ['client-withdrawal-counts'] });
    void qc.invalidateQueries({ queryKey: ['referral-stats'] });
  };

  const onDecisionError = (e: unknown, close: () => void, closedText = CLOSED_TEXT) => {
    haptic('error');
    if (errorCode(e) === CLOSED_CODE) {
      toast.error(closedText);
      close();
      refreshAll();
      return;
    }
    toast.error(e instanceof Error && e.message ? e.message : 'Не удалось сохранить решение');
  };

  // Заявка в новом статусе сразу в загруженных страницах: пока список перечитывается,
  // на карточке уже «Выплачено», а не снова «Перевожу» (второй тап дал бы 409).
  const patchRow = (item: ClientWithdrawal | null) => {
    if (!item) return;
    qc.setQueriesData<InfiniteData<ClientWithdrawalsPage>>({ queryKey: ['client-withdrawals'] }, (old) =>
      old
        ? {
            ...old,
            pages: old.pages.map((p) => ({
              ...p,
              items: p.items.map((x) => (x.id === item.id ? item : x)),
            })),
          }
        : old,
    );
  };

  const take = useMutation({
    mutationFn: (id: string) => api.takeClientWithdrawal(id),
    onSuccess: (item) => {
      haptic('success');
      toast.success('Заявка в работе, клиент её уже не отменит. Переведите деньги и нажмите «Выплачено»');
      patchRow(item);
      refreshAll();
    },
    onError: (e) => onDecisionError(e, () => {}, 'Заявку уже взяли в работу или закрыли'),
  });

  const paid = useMutation({
    mutationFn: (id: string) => api.markClientWithdrawalPaid(id),
    onSuccess: () => {
      haptic('success');
      toast.success('Отмечено: выплачено. Сумма вернулась вам в доход');
      setPaying(null);
      refreshAll();
      // Доход партнёра вырос: баланс в профиле и кошельке должен это показать.
      void qc.invalidateQueries({ queryKey: ['partner'] });
    },
    onError: (e) => onDecisionError(e, () => setPaying(null)),
  });

  const platform = useMutation({
    mutationFn: (id: string) => api.sendClientWithdrawalViaPlatform(id),
    onSuccess: (item) => {
      haptic('success');
      toast.success(
        item.ownerApproval === 'required'
          ? 'Сумма выше лимита: выплату проверит платформа'
          : 'Платформа отправляет деньги, клиенту придёт сообщение',
      );
      setPlatforming(null);
      patchRow(item);
      refreshAll();
    },
    onError: (e) => {
      if (errorCode(e) === 'app.referral_withdraw.platform') {
        haptic('error');
        toast.error(
          errorReason(e) === 'rail_off'
            ? 'Платформа сейчас не платит этим способом. Переведите сами или откажите'
            : CLOSED_TEXT,
        );
        setPlatforming(null);
        refreshAll();
        return;
      }
      onDecisionError(e, () => setPlatforming(null));
    },
  });

  const reject = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => api.rejectClientWithdrawal(id, reason),
    onSuccess: () => {
      haptic('success');
      toast.success('Заявка отклонена, деньги вернулись клиенту на баланс');
      setRejecting(null);
      refreshAll();
    },
    onError: (e) => onDecisionError(e, () => setRejecting(null)),
  });

  const rows = list.data?.pages.flatMap((p) => p.items) ?? [];
  const options = WITHDRAW_STATUSES.map((s) => {
    const n = counts?.[s.value];
    return { value: s.value, label: n === undefined ? s.label : `${s.label} ${n}` };
  });

  return (
    <div className="p-4 space-y-4 pb-28">
      <MoneySwitch />
      <div className="px-1 pt-2">
        <h2 className="font-display text-2xl font-bold tracking-tight">Выводы клиентов</h2>
        <p className="text-[12.5px] text-dim leading-relaxed mt-1.5 mb-3">
          Нажмите «Перевожу», переведите деньги по реквизитам из заявки и отметьте «Выплачено»:
          сумма вернётся вам в доход. Если переводить не будете, отклоните заявку, и деньги
          вернутся клиенту на баланс. GRAM и чек CryptoBot можно отдать платформе кнопкой в
          заявке: комиссия из суммы клиента, ваш баланс не меняется.
        </p>
        <SegmentControl<ClientWithdrawalFilter> value={status} onChange={setStatus} options={options} />
      </div>

      <div className="space-y-[11px]">
        {list.isPending ? (
          <WithdrawalsSkeleton />
        ) : list.isError && rows.length === 0 ? (
          <div className="surface-card rounded-[22px] py-8 px-5 text-center">
            <div className="font-display font-semibold text-[15px] text-muted-foreground">
              Не удалось загрузить заявки
            </div>
            <button
              type="button"
              onClick={() => void list.refetch()}
              className="mt-3 text-[13.5px] font-semibold text-primary-light"
            >
              Повторить
            </button>
          </div>
        ) : rows.length === 0 ? (
          <EmptyWithdrawals
            status={status}
            withdrawAvailable={stats?.withdrawAvailable}
            withdrawEnabled={stats?.withdrawEnabled}
            franchiseBot={partner?.franchiseBotUsername}
          />
        ) : (
          rows.map((w) => (
            <WithdrawalCard
              key={w.id}
              w={w}
              taking={take.isPending && take.variables === w.id}
              onTake={() => {
                haptic('light');
                take.mutate(w.id);
              }}
              onPaid={() => {
                haptic('light');
                setPaying(w);
              }}
              onReject={() => {
                haptic('light');
                setRejecting(w);
              }}
              onPlatform={() => {
                haptic('light');
                setPlatforming(w);
              }}
            />
          ))
        )}

        {list.hasNextPage && (
          <button
            type="button"
            onClick={() => {
              haptic('light');
              void list.fetchNextPage();
            }}
            disabled={list.isFetchingNextPage}
            className="w-full surface-card rounded-[18px] py-3.5 text-[13.5px] font-semibold text-primary-light disabled:opacity-60"
          >
            {list.isFetchingNextPage ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Показать ещё'}
          </button>
        )}
      </div>

      <BottomSheet
        open={paying !== null}
        onClose={() => {
          if (!paid.isPending) setPaying(null);
        }}
        title="Отметить выплату"
      >
        {paying && (
          <>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Вы уже перевели{' '}
              <span className="font-semibold text-foreground nums">
                {formatDisplayMinor(paying.amountDisplayMinor, paying.displayCurrency)}
              </span>{' '}
              на {withdrawTarget(paying.method, paying.destination)}? Нажимайте, только если перевод
              ушёл.
            </p>
            <p className="text-[12.5px] text-dim leading-relaxed">
              Сумма вернётся вам в доход, клиенту придёт сообщение, что деньги отправлены. Отменить
              отметку нельзя.
            </p>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setPaying(null)}
                disabled={paid.isPending}
                className="bg-white/[0.06] text-foreground py-3.5 rounded-[14px] text-sm font-bold active:scale-[0.98] transition-transform disabled:opacity-50"
              >
                Отмена
              </button>
              <button
                type="button"
                onClick={() => paid.mutate(paying.id)}
                disabled={paid.isPending}
                className="bg-money text-[#04140d] py-3.5 rounded-[14px] text-sm font-bold active:scale-[0.98] transition-transform disabled:opacity-60"
              >
                {paid.isPending ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Да, выплачено'}
              </button>
            </div>
          </>
        )}
      </BottomSheet>

      <BottomSheet
        open={platforming !== null}
        onClose={() => {
          if (!platform.isPending) setPlatforming(null);
        }}
        title="Выплатить через платформу"
      >
        {platforming && (
          <>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Платформа сама переведёт{' '}
              {platforming.method === 'ton' ? 'GRAM на кошелёк клиента' : 'чек CryptoBot клиенту в Telegram'}.
              {platforming.netDisplayMinor != null && (
                <>
                  {' '}
                  Клиент получит{' '}
                  <span className="font-semibold text-foreground nums">
                    {formatDisplayMinor(platforming.netDisplayMinor, platforming.displayCurrency)}
                  </span>{' '}
                  на руки, комиссия сети из его суммы.
                </>
              )}
            </p>
            <p className="text-[12.5px] text-dim leading-relaxed">
              Ваш баланс не изменится: бонус уже списан с вашей маржи. Перевести самому после
              этого будет нельзя.
            </p>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setPlatforming(null)}
                disabled={platform.isPending}
                className="bg-white/[0.06] text-foreground py-3.5 rounded-[14px] text-sm font-bold active:scale-[0.98] transition-transform disabled:opacity-50"
              >
                Отмена
              </button>
              <button
                type="button"
                onClick={() => platform.mutate(platforming.id)}
                disabled={platform.isPending}
                className="bg-money text-[#04140d] py-3.5 rounded-[14px] text-sm font-bold active:scale-[0.98] transition-transform disabled:opacity-60"
              >
                {platform.isPending ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Да, выплатить'}
              </button>
            </div>
          </>
        )}
      </BottomSheet>

      <BottomSheet
        open={rejecting !== null}
        onClose={() => {
          if (!reject.isPending) setRejecting(null);
        }}
        title="Отклонить заявку"
      >
        {rejecting && (
          <RejectForm
            key={rejecting.id}
            w={rejecting}
            pending={reject.isPending}
            onCancel={() => setRejecting(null)}
            onSubmit={(reason) => reject.mutate({ id: rejecting.id, reason })}
          />
        )}
      </BottomSheet>
    </div>
  );
}

/** Что сейчас с выплатой платформой: одна строка под суммой. */
function platformStatusText(w: ClientWithdrawal): string {
  if (w.status === 'paid') return 'Выплатила платформа';
  if (w.ownerApproval === 'required') return 'Сумма выше лимита: выплату проверяет платформа';
  if (w.transferState === 'sent') return 'GRAM отправлены, ждём подтверждения сети';
  if (w.transferState === 'unknown') return 'Платформа проверяет, дошли ли деньги';
  if (w.transferState === 'failed') return 'Перевод не прошёл, платформа повторит';
  return 'Платформа отправляет деньги';
}

function WithdrawalCard({
  w,
  taking,
  onTake,
  onPaid,
  onReject,
  onPlatform,
}: {
  w: ClientWithdrawal;
  taking: boolean;
  onTake: () => void;
  onPaid: () => void;
  onReject: () => void;
  onPlatform: () => void;
}) {
  const byPlatform = w.payoutMode === 'platform';
  const [copied, setCopied] = useState(false);
  const masked = isMaskedDestination(w.destination);
  const copy = async () => {
    if (await copyText(w.destination)) {
      haptic('success');
      setCopied(true);
      toast.success('Реквизиты скопированы');
      setTimeout(() => setCopied(false), 1500);
    } else {
      toast.error('Не удалось скопировать, выделите реквизиты вручную');
    }
  };

  return (
    <div className="surface-card rounded-[20px] p-[15px] space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-bold text-[15px] truncate">{w.clientLabel}</div>
          <div className="text-[11.5px] text-dim truncate mt-0.5">
            {w.botUsername ? `@${w.botUsername} · ` : ''}
            {fmtDateTime(w.createdAt)}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="font-display font-bold text-[18px] nums">
            {formatDisplayMinor(w.amountDisplayMinor, w.displayCurrency)}
          </div>
          {w.method === 'usdt_trc20' && (
            <div className="text-[11px] text-dim nums">≈ {formatUsdt(w.amountUsdMinor)}</div>
          )}
        </div>
      </div>

      <div>
        <div className="eyebrow mb-1.5">
          {withdrawMethodShort(w.method)}
          {w.method === 'cryptobot' ? ' · Telegram ID клиента' : ''}
        </div>
        {masked ? (
          // Закрытая заявка: бэк отдаёт реквизиты замаскированными, копировать нечего.
          <div className="w-full surface-inset rounded-[14px] px-3.5 py-3">
            <code className="font-mono text-[12.5px] text-muted-foreground break-all">{w.destination}</code>
          </div>
        ) : (
          <button
            type="button"
            onClick={copy}
            className="w-full surface-inset rounded-[14px] px-3.5 py-3 flex items-center gap-2 active:scale-[0.99] transition-transform text-left"
            aria-label="Скопировать реквизиты"
          >
            <code className="font-mono text-[12.5px] text-foreground break-all flex-1 select-all">{w.destination}</code>
            {copied ? (
              <Check className="w-4 h-4 text-money-light shrink-0" />
            ) : (
              <Copy className="w-4 h-4 text-primary-light shrink-0" />
            )}
          </button>
        )}
      </div>

      {w.memo && (
        <div className="text-[12px] text-dim break-all">
          Memo: <code className="font-mono text-foreground select-all">{w.memo}</code>
        </div>
      )}

      <div className="text-[12px] text-dim">
        Пригласил {w.invited}, платили {w.paying}, заработал {w.earnedRub.toLocaleString('ru-RU')} ₽
      </div>

      {byPlatform && (
        <div className="rounded-[12px] bg-white/[0.07] px-3 py-2 space-y-0.5">
          {/* У выплаченной итог уже написан внизу карточки: второй раз не повторяем. */}
          {w.status !== 'paid' && (
            <div className="text-[12px] font-semibold text-primary-light">{platformStatusText(w)}</div>
          )}
          {w.netDisplayMinor != null && (
            <div className="text-[11.5px] text-dim nums">
              Клиенту на руки {formatDisplayMinor(w.netDisplayMinor, w.displayCurrency)}
            </div>
          )}
          {w.txHash && (
            <a
              href={`https://tonviewer.com/transaction/${w.txHash}`}
              target="_blank"
              rel="noreferrer"
              className="text-[11.5px] font-semibold text-primary-light"
            >
              Перевод в сети TON
            </a>
          )}
        </div>
      )}

      {w.status === 'rejected' && w.rejectReason && (
        <div className="rounded-[12px] bg-[rgba(248,113,113,0.08)] px-3 py-2 text-[12px] text-[#f8a3a3]">
          Причина отказа: {w.rejectReason}
        </div>
      )}
      {(w.status === 'paid' || w.status === 'rejected' || w.status === 'cancelled') && (
        <div className="text-[11.5px] text-dim">
          {w.status === 'paid'
            ? byPlatform
              ? 'Выплатила платформа'
              : 'Выплачено'
            : w.status === 'rejected'
              ? 'Отклонено'
              : 'Клиент отменил заявку'}
          {w.decidedAt ? ` ${fmtDateTime(w.decidedAt)}` : ''}
        </div>
      )}

      {w.status === 'pending' && (
        <>
          {w.platformAvailable && (
            <button
              type="button"
              onClick={onPlatform}
              disabled={taking}
              className="w-full bg-money text-[#04140d] py-3 rounded-[14px] text-sm font-bold active:scale-[0.98] transition-transform disabled:opacity-60"
            >
              Выплатить через платформу
            </button>
          )}
          <p className="text-[11.5px] text-dim leading-relaxed">
            {w.platformAvailable
              ? 'Или переведите сами: нажмите «Перевожу» перед переводом, чтобы клиент не отменил заявку.'
              : 'Нажмите «Перевожу» перед переводом: пока вы переводите, клиент не сможет отменить заявку.'}
          </p>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={onReject}
              disabled={taking}
              className="bg-white/[0.06] text-foreground py-3 rounded-[14px] text-sm font-bold active:scale-[0.98] transition-transform disabled:opacity-50"
            >
              Отклонить
            </button>
            <button
              type="button"
              onClick={onTake}
              disabled={taking}
              className="bg-primary text-primary-foreground py-3 rounded-[14px] text-sm font-bold active:scale-[0.98] transition-transform disabled:opacity-60"
            >
              {taking ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Перевожу'}
            </button>
          </div>
        </>
      )}

      {w.status === 'processing' && !byPlatform && (
        <>
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <span className="rounded-full bg-white/[0.07] px-2.5 py-0.5 text-[11.5px] font-semibold text-primary-light">
                Перевожу
              </span>
              {w.takenAt && <span className="text-[11.5px] text-dim nums">{fmtDateTime(w.takenAt)}</span>}
            </div>
            <p className="text-[11.5px] text-dim leading-relaxed">
              Клиент уже не отменит заявку. Когда перевод уйдёт, нажмите «Выплачено».
            </p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={onReject}
              className="bg-white/[0.06] text-foreground py-3 rounded-[14px] text-sm font-bold active:scale-[0.98] transition-transform"
            >
              Отклонить
            </button>
            <button
              type="button"
              onClick={onPaid}
              className="bg-money text-[#04140d] py-3 rounded-[14px] text-sm font-bold active:scale-[0.98] transition-transform"
            >
              Выплачено
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function RejectForm({
  w,
  pending,
  onCancel,
  onSubmit,
}: {
  w: ClientWithdrawal;
  pending: boolean;
  onCancel: () => void;
  onSubmit: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');
  const trimmed = reason.trim();
  return (
    <>
      <p className="text-sm text-muted-foreground leading-relaxed">
        {formatDisplayMinor(w.amountDisplayMinor, w.displayCurrency)} вернутся клиенту {w.clientLabel}{' '}
        на баланс в боте. Причину он увидит в сообщении.
      </p>
      {w.status === 'processing' && (
        <p className="text-[12.5px] text-[#f0b04e] leading-relaxed">
          Если деньги уже ушли, не отклоняйте заявку, а отметьте «Выплачено»: иначе клиент получит
          сумму дважды.
        </p>
      )}
      <div className="space-y-1.5">
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value.slice(0, REJECT_REASON_MAX))}
          rows={4}
          maxLength={REJECT_REASON_MAX}
          placeholder="Например: реквизиты не проходят, пришлите другую карту"
          className="w-full surface-inset rounded-[14px] px-3.5 py-3 text-sm outline-none resize-none placeholder:text-dim"
        />
        <div className="text-right font-mono text-[10.5px] text-dim">
          {reason.length}/{REJECT_REASON_MAX}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={pending}
          className="bg-white/[0.06] text-foreground py-3.5 rounded-[14px] text-sm font-bold active:scale-[0.98] transition-transform disabled:opacity-50"
        >
          Отмена
        </button>
        <button
          type="button"
          onClick={() => onSubmit(trimmed)}
          disabled={pending || trimmed.length === 0}
          className="bg-danger text-white py-3.5 rounded-[14px] text-sm font-bold active:scale-[0.98] transition-transform disabled:opacity-50"
        >
          {pending ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Отклонить'}
        </button>
      </div>
    </>
  );
}

function EmptyWithdrawals({
  status,
  withdrawAvailable,
  withdrawEnabled,
  franchiseBot,
}: {
  status: ClientWithdrawalFilter;
  withdrawAvailable?: boolean;
  withdrawEnabled?: boolean;
  franchiseBot?: string | null;
}) {
  let title = 'Здесь пока пусто';
  let text = 'Заявки появятся здесь, когда по ним будет решение.';
  let action = false;
  if (withdrawAvailable === false) {
    title = 'Вывод для клиентов недоступен';
    text = 'У зеркал вывод реферального заработка деньгами не подключается.';
  } else if (status === 'pending' && withdrawEnabled === false) {
    title = 'Вывод для клиентов выключен';
    text = 'Включите его в настройках рефералки: клиенты смогут выводить заработанное деньгами.';
    action = true;
  } else if (status === 'pending') {
    title = 'Новых заявок нет';
    text = `Когда клиент закажет вывод, заявка появится здесь и придёт вам в личку ${
      franchiseBot ? `@${franchiseBot}` : 'франшизного бота'
    }.`;
  } else if (status === 'cancelled') {
    text = 'Здесь будут заявки, которые клиенты отменили сами.';
  }
  return (
    <div className="surface-card rounded-[22px] py-10 px-5 text-center">
      <div className="w-[52px] h-[52px] rounded-[16px] bg-white/[0.05] flex items-center justify-center mx-auto mb-3.5 text-[#6b6d75]">
        <HandCoins className="w-[26px] h-[26px]" strokeWidth={1.6} />
      </div>
      <div className="font-display font-semibold text-[15px] text-muted-foreground">{title}</div>
      <div className="text-[12.5px] text-dim mt-1.5">{text}</div>
      {action && (
        <Link
          to="/referrals"
          onClick={() => haptic('light')}
          className="inline-block mt-4 text-[13.5px] font-semibold text-primary-light"
        >
          Открыть настройки рефералки
        </Link>
      )}
    </div>
  );
}

/** Карточка заявки: клиент и сумма, реквизиты, след по рефералке, подсказка, две кнопки. */
function WithdrawalsSkeleton() {
  return (
    <>
      {[0, 1, 2].map((i) => (
        <SkeletonCard key={i} className="rounded-[20px] p-[15px] space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-2 flex-1">
              <SkeletonLine size="lg" className="w-2/5" />
              <SkeletonLine size="xs" className="w-1/3" />
            </div>
            <SkeletonLine size="title" className="w-20" />
          </div>
          <Skeleton className="h-[46px] w-full rounded-[14px]" />
          <SkeletonLine size="sm" className="w-3/5" />
          <SkeletonLine size="xs" className="w-4/5" />
          <div className="grid grid-cols-2 gap-2">
            <Skeleton className="h-[44px] rounded-[14px]" />
            <Skeleton className="h-[44px] rounded-[14px]" />
          </div>
        </SkeletonCard>
      ))}
    </>
  );
}
